import * as React from 'react';
import { Tag } from 'antd';
import { DownOutlined, RightOutlined } from '@ant-design/icons';
import { TableProps, ColumnProps } from 'antd/lib/table';
import HistoryModal from '../../../custom_shared/components/HistoryModal/HistoryModal';
import {
    CONTRACT_HISTORY_PAGE_SIZE,
    ContractAuditDto,
    ContractChangeDto,
    ContractHistoryPageDto,
    ContractHistoryTargetDto
} from '../../history/types';
import {
    contractActorName,
    contractHistoryTarget,
    contractHistoryValue,
    formatContractTimestamp
} from '../../history/presentation';
import './ContractChangesModal.less';

interface Props {
    audit?: ContractAuditDto | null;
    names: Readonly<Record<string, string>>;
    visible: boolean;
    onClose: () => void;
    afterClose: () => void;
    loadPage: (page: number, ascending: boolean, throughSequence?: number) => Promise<ContractHistoryPageDto>;
    onPageActors?: (ids: string[]) => void;
}

interface HistoryRow {
    key: string;
    change: ContractChangeDto;
    userId?: string | null;
    completedAt?: string | null;
    trigger?: ContractHistoryTargetDto;
    children?: HistoryRow[];
}

function automaticChangesLabel(row: HistoryRow) {
    const count = row.children?.length ?? 0;
    return `${count} automatic change${count === 1 ? '' : 's'}`;
}

export default function ContractChangesModal({
    audit,
    names,
    visible,
    onClose,
    afterClose,
    loadPage,
    onPageActors
}: Props): JSX.Element {
    const [order, setOrder] = React.useState<'ascend' | 'descend'>('descend');
    const [page, setPage] = React.useState(1);
    const [expandedKeys, setExpandedKeys] = React.useState<readonly React.Key[]>([]);
    const [loaded, setLoaded] = React.useState<ContractHistoryPageDto>();
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<string>();
    const [retry, setRetry] = React.useState(0);
    const throughSequence = React.useRef<number>();
    const wasVisible = React.useRef(false);

    React.useEffect(() => {
        setOrder('descend');
        setPage(1);
        setExpandedKeys([]);
        setLoaded(undefined);
        setLoading(false);
        setError(undefined);
        setRetry(0);
        throughSequence.current = undefined;
    }, [visible]);

    React.useEffect(() => {
        const opening = visible && !wasVisible.current;
        wasVisible.current = visible;
        if (!visible) {
            return;
        }
        if (opening && (page !== 1 || order !== 'descend' || retry !== 0)) {
            return;
        }
        let current = true;
        setLoading(true);
        setError(undefined);
        loadPage(page, order === 'ascend', throughSequence.current)
            .then(result => {
                if (current) {
                    throughSequence.current = result.throughSequence;
                    setLoaded(result);
                    onPageActors?.([
                        ...new Set(result.changes.map(group => group.userId).filter((id): id is string => !!id))
                    ]);
                }
            })
            .catch(() => {
                if (current) {
                    setLoaded(undefined);
                    setError('Could not load Contract history.');
                }
            })
            .finally(() => {
                if (current) {
                    setLoading(false);
                }
            });
        return () => {
            current = false;
        };
    }, [visible, loadPage, onPageActors, page, order, retry]);

    const rows: HistoryRow[] = (loaded?.changes ?? []).map(group => {
        const key = JSON.stringify([group.saveId, group.id]);
        return {
            key,
            change: group.change,
            userId: group.userId,
            completedAt: group.completedAt,
            children: group.automaticChanges?.length
                ? group.automaticChanges.map((automatic, index) => ({
                      key: JSON.stringify([group.saveId, group.id, index]),
                      change: automatic.change,
                      completedAt: group.completedAt,
                      trigger: automatic.triggeredBy
                  }))
                : undefined
        };
    });

    const columns: ColumnProps<HistoryRow>[] = [
        {
            title: 'What Changed',
            key: 'target',
            width: 360,
            render: (_, row) => {
                const expanded = expandedKeys.includes(row.key);
                const target = contractHistoryTarget(row.change.target);
                const label = automaticChangesLabel(row);
                return (
                    <div className="contract-history-target">
                        <div className="contract-history-field">{target}</div>
                        {!!row.children?.length && (
                            <button
                                type="button"
                                className="contract-history-disclosure"
                                aria-expanded={expanded}
                                aria-label={`${expanded ? 'Collapse' : 'Expand'} ${label} for ${target}`}
                                onClick={() =>
                                    setExpandedKeys(keys =>
                                        keys.includes(row.key)
                                            ? keys.filter(key => key !== row.key)
                                            : [...keys, row.key]
                                    )
                                }
                            >
                                {expanded ? <DownOutlined aria-hidden="true" /> : <RightOutlined aria-hidden="true" />}{' '}
                                {label}
                            </button>
                        )}
                    </div>
                );
            }
        },
        {
            title: 'Triggered By',
            key: 'trigger',
            width: 230,
            render: (_, row) => (
                <Tag color={row.trigger ? 'blue' : 'green'}>
                    {row.trigger ? contractHistoryTarget(row.trigger) : 'User'}
                </Tag>
            )
        },
        { title: 'Old Value', key: 'old', width: 185, render: (_, row) => contractHistoryValue(row.change.oldValue) },
        { title: 'New Value', key: 'new', width: 185, render: (_, row) => contractHistoryValue(row.change.newValue) },
        {
            title: 'Changed By',
            key: 'actor',
            width: 170,
            render: (_, row) => (row.trigger ? 'Automatic' : contractActorName(row.userId, names))
        },
        {
            title: 'Date',
            key: 'date',
            width: 190,
            sorter: true,
            sortOrder: order,
            sortDirections: ['descend', 'ascend', 'descend'],
            render: (_, row) => formatContractTimestamp(row.completedAt)
        }
    ];
    const onChange: TableProps<HistoryRow>['onChange'] = (pagination, _, sorter, extra) => {
        if (extra.action === 'sort') {
            setOrder(!Array.isArray(sorter) && sorter.order === 'ascend' ? 'ascend' : 'descend');
            setPage(1);
        } else {
            setPage(pagination.current ?? 1);
        }
    };

    return (
        <HistoryModal<HistoryRow>
            title="Contract Changes"
            className="contract-changes-modal"
            visible={visible}
            onClose={onClose}
            afterClose={afterClose}
            rows={rows}
            loading={loading}
            scrollX={columns.reduce((sum, column) => sum + Number(column.width ?? 0), 0)}
            columns={columns}
            rowKey="key"
            rowClassName={row => (row.trigger ? 'contract-automatic-change' : '')}
            emptyText={audit?.isHistoryComplete === true ? 'No changes yet' : 'No recorded changes'}
            notice={
                <>
                    {audit?.isHistoryComplete !== true && <p role="note">Earlier change history is unavailable</p>}
                    {error && (
                        <p role="alert">
                            {error}{' '}
                            <button type="button" onClick={() => setRetry(value => value + 1)}>
                                Try again
                            </button>
                        </p>
                    )}
                </>
            }
            pagination={{
                current: page,
                pageSize: loaded?.pageSize ?? CONTRACT_HISTORY_PAGE_SIZE,
                total: loaded?.total ?? audit?.historyCount ?? 0,
                hideOnSinglePage: true,
                showSizeChanger: false
            }}
            onChange={onChange}
            expandable={{
                expandedRowKeys: expandedKeys,
                indentSize: 0,
                expandIcon: () => null
            }}
        />
    );
}
